import { WarningAmber } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Search } from "lucide-react";
import React, { useState } from "react";
import { Attributes, StepComponentProps } from "../step-config-file/types";

interface InsightItem {
  label: string | any;
  value: string | number | any;
  uom?: string;
}

// Generic function to handle rendering of table based on insight type and data.
const renderInsightTable = <T extends InsightItem>(
  data: T[],
  title: string,
  theme: any,
  expanded: boolean,
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void
) => (
  <Accordion
    expanded={expanded}
    onChange={onChange}
    sx={{
      "&:before": {
        display: "none",
      },
      boxShadow: "none",
      backgroundColor: "rgba(0, 0, 0, 0)",
      backgroundImage: "none",
      borderRadius: 0,
      border: "none",
      "&.Mui-expanded": {
        margin: 0,
      },
      padding: 0,
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        minHeight: "32px",
        padding: "0",
        backgroundColor: "rgba(0, 0, 0, 0)",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&.Mui-expanded": {
          minHeight: "32px",
        },
        "& .MuiAccordionSummary-content": {
          margin: 0,
          "&.Mui-expanded": {
            margin: 0,
          },
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: theme.palette.text.primary,
          textTransform: "capitalize",
        }}
      >
        {title}
      </Typography>
    </AccordionSummary>

    <AccordionDetails
      sx={{
        padding: 0,
        backgroundColor: "rgba(0, 0, 0, 0)",
        gap: "4px",
        marginTop: "4px",
        marginBottom: "4px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {data.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginLeft: "16px",
            marginRight: "16px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: theme.palette.text.secondary,
            }}
          >
            {item.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: theme.palette.text.primary,
            }}
          >
            {typeof item.value === "number"
              ? Number.isInteger(item.value)
                ? item.value
                : item.value.toFixed(2)
              : item.value}{" "}
            {item.uom ?? null}
          </Typography>
        </Box>
      ))}
    </AccordionDetails>
  </Accordion>
);

interface FeatureCardProps {
  address: string;
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOneTitle: string;
  headerTwoTitle: string;
  style: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  address,
  featureDetails,
  floodDetails,
  headerOneTitle,
  headerTwoTitle,
  style,
}) => {
  const theme = useTheme();
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({
    featureDetails: true,
    floodDetails: true,
  });
  const floodAlertThreshold = floodDetails.find(
    (x) => x.label === `${style} Year`
  )?.value;

  const shouldShowAlert = floodAlertThreshold > 0;

  const handleAccordionChange = (accordionKey: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: isExpanded,
    }));
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        bottom: 16,
        zIndex: 900,
        width: "380px",
        overflow: "auto",
      }}
    >
      <Card
        sx={{
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(33, 33, 33, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Search size={32} color={theme.palette.text.primary} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  Feature Inspector
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {address}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
            }}
          >
            {renderInsightTable(
              featureDetails,
              headerOneTitle,
              theme,
              expandedAccordions.featureDetails,
              handleAccordionChange("featureDetails")
            )}

            {shouldShowAlert && (
              <Alert
                variant="outlined"
                icon={<WarningAmber />}
                color="warning"
                sx={{
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                At risk of {style} year flood
              </Alert>
            )}

            {renderInsightTable(
              floodDetails,
              headerTwoTitle,
              theme,
              expandedAccordions.floodDetails,
              handleAccordionChange("floodDetails")
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

interface CardContent {
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOne: string;
  headerTwo: string;
}

const contentForCard = (
  attributes: Attributes,
  type: "flood-building-height" | "flood-cadastre" | null
): CardContent => {
  if (!attributes) {
    return {
      featureDetails: [],
      floodDetails: [],
      headerOne: "",
      headerTwo: "",
    };
  }
  const properties = attributes.properties;

  if (type === "flood-building-height") {
    const featureDetails = [
      {
        label: "Ground Elevation",
        value: properties.ground_elevation,
        uom: "m",
      },
      {
        label: "First Floor Elevation",
        value: properties.first_floor_elevation,
        uom: "m",
      },
      {
        label: "Roof Height",
        value: properties.roof_height,
        uom: "m",
      },
      {
        label: "Estimated Levels",
        value: properties.estimated_levels,
      },
      {
        label: "Swimming Pool Adjacent",
        value: properties.swimming_pool_adjacent,
      },

      {
        label: "Solar Panels",
        value: properties.solar_panel,
      },

      {
        label: "Planning Zone",
        value: properties.planning_zone,
      },
      {
        label: "Building PID",
        value: properties.building_pid,
      },
      {
        label: "Area",
        value: properties.area,
        uom: "m²",
      },
      {
        label: "Basement",
        value: properties.basement,
      },
      {
        label: "Garage",
        value: properties.garage,
      },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return {
          label: `${x} Year`,
          value: modifiedVal,
          uom: "m",
        };
      }
    );

    return {
      featureDetails: featureDetails,
      floodDetails: floodDetails,
      headerOne: `Primary Buildings Details`,
      headerTwo: "Flood Height",
    };
  }

  if (type === "flood-cadastre") {
    const featureDetails: InsightItem[] = [
      {
        label: "Parcel ID",
        value: properties.parcel_id,
      },
      {
        label: "Lot",
        value: properties.lot,
      },
      {
        label: "Plan",
        value: properties.plan,
      },
      {
        label: "Title Status",
        value: properties.title_status,
      },
      {
        label: "Parcel Type",
        value: properties.parcel_type,
      },
      {
        label: "Cadastre PID",
        value: properties.cadastre_pid,
      },
      {
        label: "Area",
        value: properties.area,
        uom: "m²",
      },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return {
          label: `${x} Year`,
          value: modifiedVal,
          uom: "m",
        };
      }
    );
    return {
      featureDetails: featureDetails,
      floodDetails: floodDetails,
      headerOne: `Primary Cadastre Details`,
      headerTwo: "Flood height",
    };
  }
  return {
    featureDetails: [],
    floodDetails: [],
    headerOne: "",
    headerTwo: "",
  };
};

export const FeatureCardComponent = (props: StepComponentProps) => {
  //   @ts-ignore
  const { style } = props.formState;

  const cardContent = contentForCard(
    props.attributes,
    props.selectedLayer as "flood-building-height" | "flood-cadastre" | null
  );
  const populatedAttributes = props.attributes
    ? Object.values(props.attributes.properties).length > 0
    : false;

  if (props.attributes && populatedAttributes) {
    return (
      <FeatureCard
        address={props.attributes.properties.address}
        featureDetails={cardContent.featureDetails}
        floodDetails={cardContent.floodDetails}
        headerOneTitle={cardContent.headerOne}
        headerTwoTitle={cardContent.headerTwo}
        style={style}
      />
    );
  }

  return null;
};
